import { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';

/**
 * usePatternMemory(userId)
 *
 * Historical intelligence layer. Reads the last ~90 days of
 * symptom_logs + daily_checkins and derives cross-cycle observations
 * the Smart Suggestions, Cycle Timeline and Reports screens all consume.
 *
 * Returns:
 *   { loading, symptomHistory, recoverySeries, observations }
 *
 * observations = array of Theo-style pattern notes:
 *   { key, headline, evidence, confidence:'high'|'med'|'low', tone }
 *
 * Zero new tables — reads existing symptom_logs + daily_checkins.
 */

const DAY_MS = 86400_000;

const isoDaysAgo = (n) => new Date(Date.now() - n * DAY_MS).toISOString().split('T')[0];

const avg = (arr) => arr.length ? arr.reduce((a, b) => a + b, 0) / arr.length : 0;

async function fetchSymptomHistory(userId, days = 90) {
  const from = isoDaysAgo(days);
  const { data } = await supabase
    .from('symptom_logs')
    .select('log_date, acne, hair_loss, mood, fatigue, sleep_quality, weight_change')
    .eq('user_id', userId)
    .gte('log_date', from)
    .order('log_date', { ascending: true });
  return data || [];
}

async function fetchCheckinSeries(userId, days = 90) {
  const from = isoDaysAgo(days);
  const { data } = await supabase
    .from('daily_checkins')
    .select('log_date, mood, energy_level, pain_level, symptoms')
    .eq('user_id', userId)
    .gte('log_date', from)
    .order('log_date', { ascending: true });
  return data || [];
}

// Rolling 7-day recovery score (0-100) from fatigue + sleep_quality columns.
function computeRecoverySeries(history) {
  const scored = history.map((r) => {
    const f = Math.max(0, Math.min(10, r.fatigue ?? 5));
    const s = Math.max(0, Math.min(10, r.sleep_quality ?? 5));
    return { date: r.log_date, score: Math.round(((10 - f) + s) / 2 * 10) };
  });
  return scored;
}

function deriveObservations({ symptomHistory, recoverySeries, checkins }) {
  const obs = [];
  const now = Date.now();
  const withinDays = (dateStr, n) => (now - new Date(dateStr).getTime()) / DAY_MS <= n;

  // 1) Recovery trend last 3–7 days
  const recent = recoverySeries.slice(-7);
  const early  = recoverySeries.slice(-14, -7);
  if (recent.length >= 3 && early.length >= 3) {
    const dRecent = avg(recent.map(r => r.score));
    const dEarly  = avg(early.map(r => r.score));
    if (dRecent - dEarly >= 10) {
      obs.push({ key: 'recovery_up', tone: 'brand', confidence: 'high',
        headline: 'Recovery has improved this week.',
        evidence: `Your 7-day average went from ${Math.round(dEarly)} to ${Math.round(dRecent)}.` });
    } else if (dEarly - dRecent >= 10) {
      obs.push({ key: 'recovery_down', tone: 'warn', confidence: 'high',
        headline: 'Recovery has declined this week.',
        evidence: `Your 7-day average dropped from ${Math.round(dEarly)} to ${Math.round(dRecent)}.` });
    }
  }

  // 2) Symptom recurrence — most frequent non-zero symptom in last 30d
  const symsLast30 = symptomHistory.filter(r => withinDays(r.log_date, 30));
  if (symsLast30.length >= 5) {
    const buckets = {};
    ['acne', 'hair_loss', 'mood', 'fatigue', 'sleep_quality', 'weight_change'].forEach(k => {
      const n = symsLast30.filter(r => (r[k] || 0) >= 2).length;
      if (n >= 3) buckets[k] = n;
    });
    const top = Object.entries(buckets).sort((a, b) => b[1] - a[1])[0];
    if (top) {
      const [name, n] = top;
      obs.push({ key: 'symptom_recurrence', tone: 'brand', confidence: n >= 6 ? 'high' : 'med',
        headline: `${prettySymptom(name)} keeps returning.`,
        evidence: `You have logged ${prettySymptom(name).toLowerCase()} at moderate-or-higher intensity on ${n} of the last 30 days.` });
    }
  }

  // 3) Sleep decline pattern
  const sleepScores = symptomHistory.filter(r => withinDays(r.log_date, 14)).map(r => r.sleep_quality ?? null).filter(v => v != null);
  if (sleepScores.length >= 5) {
    const first = avg(sleepScores.slice(0, Math.floor(sleepScores.length / 2)));
    const last  = avg(sleepScores.slice(Math.floor(sleepScores.length / 2)));
    if (first - last >= 2) {
      obs.push({ key: 'sleep_decline', tone: 'warn', confidence: 'med',
        headline: 'Sleep quality is trending down.',
        evidence: `Your sleep scores in the last week are averaging ${last.toFixed(1)}/10 vs ${first.toFixed(1)}/10 the week prior.` });
    }
  }

  // 4) Energy pattern
  const energyLast7 = checkins.slice(-7).map(c => c.energy_level).filter(v => v != null);
  if (energyLast7.length >= 3) {
    const e = avg(energyLast7);
    if (e >= 7) obs.push({ key: 'energy_high', tone: 'brand', confidence: 'med',
      headline: 'Your energy is running strong.',
      evidence: `7-day energy average is ${e.toFixed(1)}/10.` });
    else if (e <= 3) obs.push({ key: 'energy_low', tone: 'warn', confidence: 'med',
      headline: 'Your energy has been low this week.',
      evidence: `7-day energy average is ${e.toFixed(1)}/10 — a signal to protect rest.` });
  }

  // 5) Consistency reward
  const logDaysLast14 = new Set(symptomHistory.filter(r => withinDays(r.log_date, 14)).map(r => r.log_date)).size;
  if (logDaysLast14 >= 10) {
    obs.push({ key: 'consistency', tone: 'brand', confidence: 'high',
      headline: `You have logged ${logDaysLast14} of the last 14 days.`,
      evidence: 'This is the level of consistency that unlocks meaningful pattern detection.' });
  }
  return obs;
}

const prettySymptom = (k) => ({
  acne: 'Acne', hair_loss: 'Hair loss', mood: 'Mood shifts', fatigue: 'Fatigue',
  sleep_quality: 'Poor sleep', weight_change: 'Weight change',
}[k] || k);

export default function usePatternMemory(userId) {
  const [state, setState] = useState({
    loading: true, symptomHistory: [], recoverySeries: [], checkins: [], observations: []
  });

  useEffect(() => {
    let alive = true;
    (async () => {
      if (!userId) { if (alive) setState((s) => ({ ...s, loading: false })); return; }
      const [symptomHistory, checkins] = await Promise.all([
        fetchSymptomHistory(userId, 90),
        fetchCheckinSeries(userId, 90),
      ]);
      if (!alive) return;
      const recoverySeries = computeRecoverySeries(symptomHistory);
      const observations = deriveObservations({ symptomHistory, recoverySeries, checkins });
      setState({ loading: false, symptomHistory, recoverySeries, checkins, observations });
    })();
    return () => { alive = false; };
  }, [userId]);

  return state;
}
